import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Activity, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeToggle } from '@/components/ThemeToggle';

const links = [
  { id: 'plataforma', label: { es: 'Plataforma', en: 'Platform' } },
  { id: 'producto', label: { es: 'Producto', en: 'Product' } },
  { id: 'empresa', label: { es: 'Empresa', en: 'Company' } },
  { id: 'equipo', label: { es: 'Equipo', en: 'Team' } },
  { id: 'conocimiento', label: { es: 'Conocimiento', en: 'Knowledge' } },
];

export const LandingNav = () => {
  const { tx, lang, toggleLang } = useApp();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const navText = scrolled
    ? 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white'
    : 'text-white/80 hover:text-white';
  const quietBtn = scrolled
    ? 'text-zinc-600 hover:bg-zinc-900/[0.05] dark:text-zinc-300 dark:hover:bg-white/[0.06]'
    : 'text-white/80 hover:bg-white/[0.08]';
  const borderBtn = scrolled
    ? 'border-zinc-300 text-zinc-700 hover:bg-zinc-900/[0.04] dark:border-white/15 dark:text-white dark:hover:bg-white/[0.08]'
    : 'border-white/25 text-white hover:bg-white/[0.08]';

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'py-2' : 'py-4'}`}
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          className={`flex items-center justify-between h-14 px-4 sm:px-5 rounded-2xl transition-all duration-300 ${
            scrolled ? 'landing-nav-scrolled backdrop-blur-xl border shadow-lg' : 'bg-transparent'
          }`}
        >
          <button onClick={() => scrollTo('hero')} className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Activity className="w-5 h-5 !text-white" strokeWidth={2.5} />
            </div>
            <span className={`font-heading font-extrabold text-lg tracking-tight ${scrolled ? 'text-zinc-900 dark:text-white' : 'text-white'}`}>FINPULSE</span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`landing-link-${l.id}`}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${navText}`}
              >
                {tx(l.label)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle testId="landing-theme" className={quietBtn} />
            <button onClick={toggleLang} data-testid="landing-lang" className={`hidden sm:flex items-center h-9 px-2.5 rounded-lg text-xs font-semibold transition-colors ${quietBtn}`}>
              <span className={lang === 'es' ? (scrolled ? 'text-cyan-600 dark:text-cyan-400' : 'text-cyan-300') : ''}>ES</span>
              <span className="mx-1 opacity-40">/</span>
              <span className={lang === 'en' ? (scrolled ? 'text-cyan-600 dark:text-cyan-400' : 'text-cyan-300') : ''}>EN</span>
            </button>
            <button onClick={() => navigate('/login')} data-testid="landing-login-btn" className={`hidden sm:flex items-center h-9 px-4 rounded-lg text-sm font-medium border transition-colors ${borderBtn}`}>
              {tx({ es: 'Iniciar sesión', en: 'Sign in' })}
            </button>
            <button onClick={() => scrollTo('demo')} data-testid="landing-demo-btn" className="flex items-center h-9 px-4 rounded-lg text-sm font-semibold !text-white bg-cyan-500 hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/25">
              {tx({ es: 'Solicitar demo', en: 'Book a demo' })}
            </button>
            <button onClick={() => setOpen((o) => !o)} className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${quietBtn}`} data-testid="landing-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="lg:hidden mt-2 rounded-2xl landing-surface backdrop-blur-xl border p-3 text-zinc-800 dark:text-zinc-100">
              {links.map((l) => (
                <button key={l.id} onClick={() => scrollTo(l.id)} className="w-full text-left px-4 py-3 rounded-lg text-sm hover:bg-zinc-900/[0.04] dark:hover:bg-white/[0.06]">{tx(l.label)}</button>
              ))}
              <div className="flex gap-2 mt-2 px-1">
                <button onClick={() => navigate('/login')} className="flex-1 h-10 rounded-lg text-sm font-medium border border-zinc-300 dark:border-white/15">{tx({ es: 'Iniciar sesión', en: 'Sign in' })}</button>
                <ThemeToggle className="border border-zinc-300 dark:border-white/15 text-zinc-700 dark:text-zinc-200" />
                <button onClick={toggleLang} className="h-10 px-4 rounded-lg text-sm font-semibold border border-zinc-300 dark:border-white/15">{lang.toUpperCase()}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
